import { useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { v1Client } from '../api'
import { useAuth } from '../providers'
import { useSetQueueCancelling } from '../store'
import type { QueuedMessage } from '../store'

export const useStopMessage = () => {
  const { getAuthToken } = useAuth()

  return useCallback(
    async (conversationId: string): Promise<boolean> => {
      try {
        const response = await v1Client.POST('/chat/{id}/stop', {
          params: {
            path: {
              id: conversationId,
            },
          },
          headers: {
            Authorization: `Bearer ${await getAuthToken()}`,
          },
        })

        if (response.error) {
          // Stringified: the error is an object, and `${response.error}` logged
          // "[object Object]" — which is how a 400 here went unnoticed.
          throw new Error(`Failed to stop message: ${JSON.stringify(response.error)}`)
        }
        return true
      } catch (error) {
        console.error('error stopping message', error)
        return false
      }
    },
    [getAuthToken],
  )
}

export const useCancelQueuedMessage = (chatId: string | undefined) => {
  const { getAuthToken } = useAuth()
  const setCancelling = useSetQueueCancelling(chatId)

  const mutation = useMutation({
    mutationFn: async (queuedItem: QueuedMessage) => {
      if (!chatId) throw new Error('cancelQueuedMessage: chatId missing')
      if (!queuedItem.messageId) throw new Error('cancelQueuedMessage: messageId missing')

      const response = await v1Client.DELETE('/chat/{id}/queue/{messageId}', {
        params: { path: { id: chatId, messageId: queuedItem.messageId } },
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      })
      if (response.error) {
        throw new Error(`Failed to cancel queued message: ${JSON.stringify(response.error)}`)
      }
      // Local removal is driven by the held-open submission stream closing
      // (the try/finally in the SSE for-await loop). Single source of truth.
    },
    onError: (error, queuedItem) => {
      console.error('error cancelling queued message', error)
      setCancelling(queuedItem.localId, false)
    },
  })

  return (queuedItem: QueuedMessage) => {
    if (!chatId) return
    if (!queuedItem.messageId) return
    // Atomic check-and-set against the atom — guards against rapid double
    // clicks before React re-renders. setCancelling returns false when the
    // flag was already set (i.e. the first click won the race).
    if (!setCancelling(queuedItem.localId, true)) return
    mutation.mutate(queuedItem)
  }
}
